import { useQuery } from '@tanstack/react-query';
import { AuthState, useIdentify } from '@hungryroot/customer';
import { useCustomerDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import {
  getOccasionPreferenceDietaryRestrictionParamFromDietaryRestrictions,
  type MealTypePreferences,
  useGetCustomerOccasionPreferences,
  useGetMealTypePreferences,
} from '@hungryroot/exp-175-diet-driven-preferences';
import { useExperiment } from '@hungryroot/experiments';
import { useGetPreferenceOptions } from '@hungryroot/preference-options';
import { StatusCode, type BaseTag } from '@hungryroot/rest';

import { type GetTagGroupResponse } from '@hungryroot/rest/api/get_tag_group/get';
import {
  getCustomerDietaryRestrictions,
  type DietaryRestrictions as CustomerDietaryRestrictionsResponse,
} from '@hungryroot/rest/api/v2/customers/[id]/dietary_restrictions/get';
import {
  getApiV2CustomersCustomerIdDishTypes,
  type PaginatedCustomerDishTypeList as CustomerDishTypesResponse,
} from '@hungryroot/rest/api/v2/customers/[id]/dish_types/get';
import {
  getApiV2CustomersCustomerIdOccasionGoals,
  type CustomerOccasionGoal as CustomerOccasionGoalsResponse,
} from '@hungryroot/rest/api/v2/customers/[id]/occasion_goals/get';
import {
  getCustomerQuizzes,
  type QuizResponse as ServerQuizResponse,
} from '@hungryroot/rest/api/v2/customers/[id]/quizzes/get';
import {
  getApiV2CustomersCustomerIdTags,
  type PaginatedCustomerTagListList as CustomerTagsResponse,
} from '@hungryroot/rest/api/v2/customers/[id]/tags/get';
import { type GetPreferenceOptionsResponse } from '@hungryroot/rest/api/v2/preference_options/get/get_preference_options';
import { useApiV1TagGroup, tagGroups, tags } from '@hungryroot/tags';
import { LikeDislikeTags } from '../defs/quiz';
import {
  defaultQuiz,
  FoodPrep,
  type Dietary,
  type EatingHabit,
  type Goals,
  type Quiz,
} from '../defs/quiz';
import { processBinaryPreferenceOptions } from './processBinaryPreferenceOptions';
import { useLocalQuiz } from './useLocalQuiz';

const MODERATE_PREP_TAG = 146;

async function loadUserQuizFromApi(params: {
  customerId: number;
}): Promise<ServerQuizResponse> {
  const { customerId } = params;

  const resp = await getCustomerQuizzes();
  if (resp.status !== StatusCode.SuccessOK) {
    return {
      customer_id: customerId,
      household_size: defaultQuiz.householdSize,
      num_adults: defaultQuiz.adultsCount ?? 0,
      num_young_children: defaultQuiz.youngChildrenCount ?? 0,
      num_older_children: defaultQuiz.childrenCount ?? 0,
      weekly_grocery_spend: null,
      breakfast_meal_snack: null,
      lunch_meal_snack: null,
      dinner_meal_snack: null,
      breakfast_frequency: defaultQuiz.breakfastFrequency,
      lunch_frequency: defaultQuiz.lunchFrequency,
      dinner_frequency: defaultQuiz.dinnerFrequency,
      snack_frequency: defaultQuiz.snacksFrequency,
      sweets_frequency: defaultQuiz.sweetsFrequency,
      fruit_frequency: null,
      kid_snack_frequency: null,
    };
  }
  return resp.data;
}

async function loadCustomerOccasionGoals(): Promise<CustomerOccasionGoalsResponse> {
  const res = await getApiV2CustomersCustomerIdOccasionGoals();

  if (res.status !== StatusCode.SuccessOK) {
    return {
      occasion_goals: [],
    };
  }

  return res.data;
}

async function loadCustomerDishtypes(): Promise<CustomerDishTypesResponse> {
  const res = await getApiV2CustomersCustomerIdDishTypes({});

  if (res.status !== StatusCode.SuccessOK) {
    return {
      count: 0,
      next: null,
      previous: null,
      results: [],
    };
  }

  return res.data;
}

async function loadCustomerDietaryRestrictionsFromApi(): Promise<CustomerDietaryRestrictionsResponse> {
  const res = await getCustomerDietaryRestrictions();

  if (res.status !== StatusCode.SuccessOK) {
    return {
      dietary_restrictions: [],
    };
  }

  return res.data;
}

async function loadCustomerTags(params: {
  customerId: number;
}): Promise<CustomerTagsResponse> {
  const resp = await getApiV2CustomersCustomerIdTags({
    ...params,
    limit: 1000,
  });

  if (resp.status !== StatusCode.SuccessOK) {
    return {
      count: 0,
      next: null,
      previous: null,
      results: [],
    };
  }
  return resp.data;
}

function indexTags(group: GetTagGroupResponse): Record<string, BaseTag> {
  return group.tags[0].reduce(
    (seed, tag) => {
      seed[tag.id] = tag;
      return seed;
    },
    {} as Record<string, BaseTag>
  );
}

async function loadQuizFromApi(params: {
  customerId: number;
  isDietDrivenPreferencesVariant?: boolean;
  customerOccasionPreferencesByObjectId?: number[];
  cuisinesTagGroup: GetTagGroupResponse;
  flavorsTagGroup: GetTagGroupResponse;
  meatsTagGroup: GetTagGroupResponse;
  seafoodTagGroup: GetTagGroupResponse;
  plantsTagGroup: GetTagGroupResponse;
  dislikeFlavorsGroup: GetTagGroupResponse;
  macroNutrientTagGroup?: GetTagGroupResponse;
  readyMadeTagGroup?: GetTagGroupResponse;
  budgetTagGroup?: GetTagGroupResponse;
  breakfastOptionsById: MealTypePreferences | undefined;
  lunchOptionsById: MealTypePreferences | undefined;
  dinnerOptionsById: MealTypePreferences | undefined;
  snacksOptionsById: MealTypePreferences | undefined;
  sweetsOptionsById: MealTypePreferences | undefined;
  preferenceOptions?: GetPreferenceOptionsResponse;
}): Promise<Quiz> {
  const {
    isDietDrivenPreferencesVariant,
    customerOccasionPreferencesByObjectId,
    cuisinesTagGroup,
    flavorsTagGroup,
    meatsTagGroup,
    seafoodTagGroup,
    plantsTagGroup,
    dislikeFlavorsGroup,
    breakfastOptionsById,
    snacksOptionsById,
    sweetsOptionsById,
    macroNutrientTagGroup,
    readyMadeTagGroup,
    budgetTagGroup,
    lunchOptionsById,
    dinnerOptionsById,
    preferenceOptions,
  } = params;

  const [
    serverQuiz,
    dietaryRestrictions,
    customerOccasionGoals,
    customerDishTypes,
    customerTags,
  ] = await Promise.all([
    loadUserQuizFromApi(params),
    loadCustomerDietaryRestrictionsFromApi(),
    loadCustomerOccasionGoals(),
    loadCustomerDishtypes(),
    loadCustomerTags(params),
  ]);

  const {
    household_size: householdSize,
    num_adults: adultsCount,
    num_older_children: childrenCount,
    num_young_children: youngChildrenCount,
    breakfast_frequency: breakfastFrequency,
    lunch_frequency: lunchFrequency,
    dinner_frequency: dinnerFrequency,
    snack_frequency: snacksFrequency,
    sweets_frequency: sweetsFrequency,
  } = serverQuiz;

  const goals: Goals[] = customerOccasionGoals.occasion_goals.map(({ id }) => {
    return id;
  });

  const lunchDinner = customerDishTypes.results.map(({ dish_type }) => {
    return dish_type;
  });

  let habits: Quiz['habits'] = null;
  let dietary: Quiz['dietary'] = null;

  dietaryRestrictions.dietary_restrictions.forEach(({ slug }) => {
    switch (slug) {
      case 'vegetarian':
      case 'omnivore':
      case 'pescatarian':
      case 'flexitarian':
      case 'vegan':
        habits = slug as EatingHabit;
        break;
      default: {
        if (dietary === null) {
          dietary = [];
        }

        dietary.push(slug as Dietary);
      }
    }
  });

  let foodPrep: Quiz['foodPref'] = null;
  let cuisines: Quiz['cuisines'] = null;
  let flavors: Quiz['flavors'] = null;

  let meats: Quiz['meats'] = null;
  let seafood: Quiz['seafood'] = null;
  let plants: Quiz['plants'] = null;

  let dislike: Quiz['dislike'] = null;
  let macroNutrient: Quiz['macroNutrient'] = null;
  let readyMade: Quiz['readyMade'] = null;
  let budget: Quiz['budget'] = null;

  let breakfast: Quiz['breakfast'] = null;
  let lunch: Quiz['lunch'] = null;
  let dinner: Quiz['dinner'] = null;
  let snacks: Quiz['snacks'] = null;
  let sweets: Quiz['sweets'] = null;

  const cuisinesTagIndex = indexTags(cuisinesTagGroup);
  const flavorsTagIndex = indexTags(flavorsTagGroup);
  const meatsTagIndex = indexTags(meatsTagGroup);
  const seafoodTagIndex = indexTags(seafoodTagGroup);
  const plantsTagIndex = indexTags(plantsTagGroup);

  const dislikeFlavorsIndex = indexTags(dislikeFlavorsGroup);
  const macroNutrientIndex =
    macroNutrientTagGroup && indexTags(macroNutrientTagGroup);

  const readyMadeIndex = readyMadeTagGroup && indexTags(readyMadeTagGroup);
  const budgetIndex = budgetTagGroup && indexTags(budgetTagGroup);

  const preferenceOptionProcessor =
    processBinaryPreferenceOptions(preferenceOptions);

  if (
    isDietDrivenPreferencesVariant &&
    customerOccasionPreferencesByObjectId !== undefined
  ) {
    customerOccasionPreferencesByObjectId.forEach((preferenceId) => {
      // Breakfast
      if (breakfastOptionsById?.includes(preferenceId)) {
        if (breakfast === null) {
          breakfast = [];
        }

        breakfast.push(preferenceId);
      }

      // Lunch
      if (lunchOptionsById?.includes(preferenceId)) {
        if (lunch === null || lunch === undefined) {
          lunch = [];
        }

        lunch.push(preferenceId);
      }

      // Dinner
      if (dinnerOptionsById?.includes(preferenceId)) {
        if (dinner === null || dinner === undefined) {
          dinner = [];
        }

        dinner.push(preferenceId);
      }

      // Snacks
      if (snacksOptionsById?.includes(preferenceId)) {
        if (snacks === null) {
          snacks = [];
        }

        snacks.push(preferenceId);
      }

      // Sweets
      if (sweetsOptionsById?.includes(preferenceId)) {
        if (sweets === null) {
          sweets = [];
        }

        sweets.push(preferenceId);
      }
    });
  }

  customerTags.results.forEach((tag) => {
    const { tag: tagId, preference } = tag;

    preferenceOptionProcessor.handleTag(tag);

    // Food Prep
    if (tagId === MODERATE_PREP_TAG) {
      if (preference === LikeDislikeTags.Liked) {
        return;
      }
      foodPrep =
        preference === LikeDislikeTags.Disliked
          ? FoodPrep.Simple
          : FoodPrep.ChoppingOrPrepping;
      return;
    }

    // Cuisines
    if (cuisinesTagIndex[tagId] !== undefined) {
      if (cuisines === null) {
        cuisines = [];
      }

      cuisines.push({
        id: tagId,
        liked: preference,
      });
    }

    // Flavors
    if (flavorsTagIndex[tagId] !== undefined) {
      if (flavors === null) {
        flavors = [];
      }

      flavors.push({
        id: tagId,
        liked: preference,
      });
    }

    // Meat
    if (meatsTagIndex[tagId] !== undefined) {
      if (meats === null) {
        meats = [];
      }

      meats.push({
        id: tagId,
        rated: preference,
      });
    }

    // Seafood
    if (seafoodTagIndex[tagId] !== undefined) {
      if (seafood === null) {
        seafood = [];
      }

      seafood.push({
        id: tagId,
        rated: preference,
      });
    }

    // Plants
    if (plantsTagIndex[tagId] !== undefined) {
      if (plants === null) {
        plants = [];
      }

      plants.push({
        id: tagId,
        rated: preference,
      });
    }

    // Dislike
    if (dislikeFlavorsIndex[tagId] !== undefined) {
      if (dislike === null) {
        dislike = [];
      }

      if (preference === LikeDislikeTags.Disliked) {
        dislike.push(tagId);
      }
    }

    // MacroNutrients
    if (macroNutrientIndex !== undefined) {
      if (macroNutrientIndex[tagId] !== undefined) {
        if (macroNutrient === null) {
          macroNutrient = [];
        }

        macroNutrient.push(tagId);
      }
    }

    if (tagId === tags.OnlyLowSodium) {
      if (macroNutrient === null) {
        macroNutrient = [];
      }

      if (preference === LikeDislikeTags.Liked) {
        macroNutrient.push(tagId);
      }
    }

    // ReadyMade
    if (readyMadeIndex !== undefined) {
      if (readyMadeIndex[tagId] !== undefined) {
        if (readyMade === null) {
          readyMade = [];
        }

        if (preference === LikeDislikeTags.Liked) {
          readyMade.push(tagId);
        }
      }
    }

    // Budget
    if (budgetIndex !== undefined) {
      if (budgetIndex[tagId] !== undefined) {
        if (budget === null || budget === undefined) {
          budget = tagId;
        }
      }
    }

    // Breakfast
    if (
      !isDietDrivenPreferencesVariant &&
      breakfastOptionsById?.includes(tagId)
    ) {
      if (breakfast === null) {
        breakfast = [];
      }

      if (preference === LikeDislikeTags.Liked) {
        breakfast.push(tagId);
      }
    }

    // Lunch
    if (!isDietDrivenPreferencesVariant && lunchOptionsById?.includes(tagId)) {
      if (lunch === null || lunch === undefined) {
        lunch = [];
      }

      if (preference === LikeDislikeTags.Liked) {
        lunch.push(tagId);
      }
    }

    // Dinner
    if (!isDietDrivenPreferencesVariant && dinnerOptionsById?.includes(tagId)) {
      if (dinner === null || dinner === undefined) {
        dinner = [];
      }

      if (preference === LikeDislikeTags.Liked) {
        dinner.push(tagId);
      }
    }

    // Sweets
    if (!isDietDrivenPreferencesVariant && sweetsOptionsById?.includes(tagId)) {
      if (sweets === null) {
        sweets = [];
      }

      if (preference === LikeDislikeTags.Liked) {
        sweets.push(tagId);
      }
    }

    // Snacks
    if (!isDietDrivenPreferencesVariant && snacksOptionsById?.includes(tagId)) {
      if (snacks === null) {
        snacks = [];
      }

      if (preference === LikeDislikeTags.Liked) {
        snacks.push(tagId);
      }
    }
  });

  return {
    householdSize,
    adultsCount,
    youngChildrenCount,
    childrenCount,
    budget,
    breakfastFrequency,
    lunch,
    dinner,
    lunchFrequency,
    dinnerFrequency,
    snacksFrequency,
    sweetsFrequency,
    dietary,
    macroNutrient,
    goals,
    lunchDinner,
    readyMade,
    habits,
    foodPref: foodPrep,
    cuisines,
    flavors,
    meats,
    seafood,
    plants,
    sweets,
    snacks,
    dislike,
    breakfast,
    ...preferenceOptionProcessor.data,
  };
}

const QUIZ_KEY = ['quiz'];

export function useQuizLoader() {
  const identify = useIdentify();
  const isAuthenticated = identify.authState === AuthState.isAuthenticated;
  const isAnonymous = identify.authState === AuthState.isAnonymous;
  const dietDrivenPreferencesVariant = useExperiment(175);
  const isExpEnrollmentResolved = dietDrivenPreferencesVariant !== undefined;
  const isLoading =
    identify.authState === AuthState.isLoading || !isExpEnrollmentResolved;

  const isQueryEnabled = isAuthenticated && isExpEnrollmentResolved;

  const cuisinesTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.cuisine },
    {
      enabled: isQueryEnabled,
    }
  );
  const flavorsTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.flavor },
    {
      enabled: isAuthenticated && isExpEnrollmentResolved,
    }
  );
  const meatsTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.meatProtein },
    {
      enabled: isQueryEnabled,
    }
  );
  const seafoodTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.seafoodProtein },
    {
      enabled: isQueryEnabled,
    }
  );
  const plantsTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.plantProtein },
    {
      enabled: isQueryEnabled,
    }
  );
  const dislikeFlavorsGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.polarizing2 },
    {
      enabled: isQueryEnabled,
    }
  );
  const macroNutrientTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.nutrition },
    {
      enabled: isQueryEnabled,
    }
  );
  const readyMadeTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.readyMade },
    {
      enabled: isQueryEnabled,
    }
  );
  const budgetTagGroup = useApiV1TagGroup(
    { tagGroupId: tagGroups.budget },
    {
      enabled: isQueryEnabled,
    }
  );

  const { data: customerDietaryRestrictions } = useCustomerDietaryRestrictions({
    enabled: isQueryEnabled,
  });

  const dietaryRestriction =
    getOccasionPreferenceDietaryRestrictionParamFromDietaryRestrictions(
      customerDietaryRestrictions?.dietary_restrictions ?? []
    );

  const {
    breakfastOptionsById,
    lunchOptionsById,
    dinnerOptionsById,
    snacksOptionsById,
    sweetsOptionsById,
  } =
    useGetMealTypePreferences(
      dietaryRestriction,
      dietDrivenPreferencesVariant
    ) || {};

  const { data: customerOccassionPreferences } =
    useGetCustomerOccasionPreferences({
      enabled:
        isQueryEnabled &&
        dietDrivenPreferencesVariant !== undefined &&
        dietDrivenPreferencesVariant > 0,
    });
  const customerOccasionPreferencesByObjectId =
    customerOccassionPreferences?.results.map(
      (preference) => preference.object_id
    );

  const { data: preferenceOptions } = useGetPreferenceOptions();

  const localStorageQuery = useLocalQuiz({
    enabled: isAnonymous === true,
  });

  const remoteQuizQuery = useQuery({
    queryKey: [QUIZ_KEY, 'api'],
    queryFn: async () => {
      const { id } = identify.customerData as Exclude<
        typeof identify.customerData,
        undefined
      >;

      return loadQuizFromApi({
        customerId: id,
        cuisinesTagGroup: cuisinesTagGroup.data as GetTagGroupResponse,
        flavorsTagGroup: flavorsTagGroup.data as GetTagGroupResponse,
        meatsTagGroup: meatsTagGroup.data as GetTagGroupResponse,
        seafoodTagGroup: seafoodTagGroup.data as GetTagGroupResponse,
        plantsTagGroup: plantsTagGroup.data as GetTagGroupResponse,
        dislikeFlavorsGroup: dislikeFlavorsGroup.data as GetTagGroupResponse,
        macroNutrientTagGroup: macroNutrientTagGroup?.data,
        readyMadeTagGroup: readyMadeTagGroup?.data,
        budgetTagGroup: budgetTagGroup?.data,
        breakfastOptionsById,
        lunchOptionsById,
        dinnerOptionsById,
        snacksOptionsById,
        sweetsOptionsById,
        preferenceOptions,
        isDietDrivenPreferencesVariant:
          dietDrivenPreferencesVariant !== undefined &&
          dietDrivenPreferencesVariant > 0,
        customerOccasionPreferencesByObjectId,
      });
    },
    enabled:
      isQueryEnabled &&
      cuisinesTagGroup.isFetched === true &&
      flavorsTagGroup.isFetched === true &&
      meatsTagGroup.isFetched === true &&
      seafoodTagGroup.isFetched === true &&
      plantsTagGroup.isFetched === true &&
      dislikeFlavorsGroup.isFetched === true &&
      breakfastOptionsById !== undefined &&
      snacksOptionsById !== undefined &&
      sweetsOptionsById !== undefined &&
      macroNutrientTagGroup.isFetched === true &&
      readyMadeTagGroup.isFetched === true &&
      budgetTagGroup.isFetched === true &&
      lunchOptionsById !== undefined &&
      dinnerOptionsById !== undefined,
  });

  if (isLoading === true) {
    return {
      data: undefined,
      isFetching: true,
    };
  }

  if (isAuthenticated === true) {
    return remoteQuizQuery;
  }

  return {
    data: localStorageQuery,
    isFetching: localStorageQuery === undefined,
  };
}
